"use client";

import { FC, useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useSession } from "next-auth/react";
import { logoutAction } from "@/actions/logout";
import { usePasskey } from "@/contexts/PasskeyContext";
import { NavCreditsWidget } from "@/components/billing/NavCreditsWidget";

const MARKET_ITEMS = [
  {
    name: "Pools",
    path: "/pools",
    description: "Browse liquidity pools across all protocols",
    color: "#38bdf8",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M10 20H6v-2h4v2Zm8 0h-4v-2h4v2ZM6 18H2v-2h4v2Zm8 0h-4v-2h4v2Zm8 0h-4v-2h4v2Zm-12-4H6v-2h4v2Zm8 0h-4v-2h4v2ZM6 12H2v-2h4v2Zm8 0h-4v-2h4v2Zm8 0h-4v-2h4v2ZM10 8H6V6h4v2Zm8 0h-4V6h4v2ZM6 6H2V4h4v2Zm8 0h-4V4h4v2Zm8-2v2h-4V4h4Z"/>
      </svg>
    ),
  },
  {
    name: "Tokens",
    path: "/tokens",
    description: "Token prices, metrics, and market data",
    color: "#4ade80",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18 4h2v2h2v12h-2v2h-2v2H6v-2H4v-2H2V6h2V4h2V2h12v2Zm-8 4H8v8h2v2h4v-2h2V8h-2V6h-4v2Zm4 8h-4V8h4v8Z"/>
      </svg>
    ),
  },
  {
    name: "Lending",
    path: "/lending",
    description: "Blend V2 lending pools — supply & borrow rates",
    color: "#a78bfa",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <polygon points="23 20 23 22 22 22 22 23 2 23 2 22 1 22 1 20 3 20 3 19 4 19 4 10 6 10 6 19 8 19 8 10 10 10 10 19 14 19 14 10 16 10 16 19 18 19 18 10 20 10 20 19 21 19 21 20 23 20"/>
        <path d="m20,5v-1h-2v-1h-2v-1h-2v-1h-4v1h-2v1h-2v1h-2v1H1v2h1v1h1v1h18v-1h1v-1h1v-2h-3Zm-9,2v-1h-1v-2h1v-1h2v1h1v2h-1v1h-2Z"/>
      </svg>
    ),
  },
  {
    name: "Bonds",
    path: "/bonds",
    description: "Etherfuse stablebonds — tokenized government bonds",
    color: "#f472b6",
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
        <path d="m22,9v-2h-1v-2h-1v-1h-1v-1h-2v-1h-2v-1h-6v1h-2v1h-2v1h-1v1h-1v2h-1v2h-1v6h1v2h1v2h1v1h1v1h2v1h2v1h6v-1h2v-1h2v-1h1v-1h1v-2h1v-2h1v-6h-1Zm-4,3v-3h1v-1h1v1h1v4h-2v-1h-1Zm-4,7v2h-2v-3h-1v-1h-1v-3h-1v-1h-1v-1h-1v-1h-1v-1h-1v-2h1v-1h1v-1h1v-1h3v-1h2v1h1v4h-2v2h1v1h-2v-1h-2v2h3v1h4v1h1v3h-1v2h-1Z"/>
      </svg>
    ),
  },
] as const;

const TESTNET_ITEMS = [
  { name: "freighter", path: "/tests/demo" },
  { name: "passkeys", path: "/tests/demo-passkeys" },
  { name: "contracts", path: "/tests/contracts" },
] as const;

function getDiceBearUrl(seed: string, size = 28): string {
  return `https://api.dicebear.com/9.x/rings/svg?seed=${encodeURIComponent(seed)}&size=${String(size)}&backgroundColor=0a0a0a`;
}

const Navbar: FC = () => {
  const { data: session } = useSession();
  const pathname = usePathname();
  const isLoggedIn = !!session?.user.accessToken;

  const [marketsOpen, setMarketsOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [marketsHover, setMarketsHover] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [assistantSessions, setAssistantSessions] = useState<{_id: string; title: string; lastActiveAt: string}[]>([]);
  const [assistantLoaded, setAssistantLoaded] = useState(false);
  const assistantCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const marketsRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const assistantRef = useRef<HTMLDivElement>(null);

  const isMarketsActive =
    pathname.startsWith("/pools") ||
    pathname.startsWith("/tokens") ||
    pathname.startsWith("/lending") ||
    pathname.startsWith("/bonds");
  const isExplorerActive = pathname.startsWith("/explorer");
  const isSwapActive = pathname.startsWith("/swap");
  const isStrategiesActive = pathname.startsWith("/strategies");
  const isAssistantActive = pathname.startsWith("/assistant");
  const isDashboardActive = pathname === "/portfolio";

  const { contractId: liveContractId, isConnected: passkeyConnected } = usePasskey();
  const displayName = session?.user.name || session?.user.email || "";
  const hasWallet = session?.user.hasWallet || passkeyConnected;
  const smartAccountAddress = liveContractId || session?.user.smart_account_address || "";
  const avatarSeed = session?.user.id || session?.user.email || "anon";
  const avatarUrl = session?.user.avatar || getDiceBearUrl(avatarSeed);

  // Close dropdowns on route change
  useEffect(() => {
    setMarketsOpen(false);
    setUserMenuOpen(false);
    setMobileOpen(false);
    setAssistantOpen(false);
  }, [pathname]);

  // Close markets dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (marketsRef.current && !marketsRef.current.contains(e.target as Node)) {
        setMarketsOpen(false);
      }
    }
    if (marketsOpen) document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [marketsOpen]);

  // Close user menu on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [userMenuOpen]);

  // Close assistant dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (assistantRef.current && !assistantRef.current.contains(e.target as Node)) {
        setAssistantOpen(false);
      }
    }
    if (assistantOpen) document.addEventListener("mousedown", handleClick);
    return () => { document.removeEventListener("mousedown", handleClick); };
  }, [assistantOpen]);

  // Lazy-fetch assistant chat sessions on first dropdown open.
  // next-auth types `user` as non-optional on a non-null session, so `?.user`
  // is redundant once we've narrowed `session` itself.
  const accessToken = session?.user.accessToken;
  useEffect(() => {
    if (!assistantOpen || assistantLoaded || !accessToken) return;
    const apiBase = process.env.NEXT_PUBLIC_BASE_DATA_URI ?? "https://api.hoops.finance";
    fetch(`${apiBase}/ai/chat/sessions`, {
      headers: { Authorization: `Bearer ${accessToken}` },
      signal: AbortSignal.timeout(5_000),
    })
      .then((r) => r.json())
      .then((d: { sessions?: typeof assistantSessions }) => { setAssistantSessions((d.sessions ?? []).slice(0, 5)); })
      .catch(() => { /* silent — dropdown stays empty on failure */ })
      .finally(() => { setAssistantLoaded(true); });
  }, [assistantOpen, assistantLoaded, accessToken]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Nav link class helper
  const navItemCls = (active: boolean) =>
    [
      "text-[11px] font-normal tracking-[0.04em] px-3 py-[6px] rounded-[7px] transition-colors duration-150",
      active
        ? "text-[#d4cfc6]"
        : "text-[#888] hover:text-[#d4cfc6] hover:bg-[#141414]",
    ].join(" ");

  const mobileLinkCls = (active: boolean) =>
    [
      "py-3 text-[12px] tracking-[0.08em] font-normal transition-colors",
      active ? "text-[#d4cfc6]" : "text-[#666] hover:text-[#d4cfc6]",
    ].join(" ");

  return (
    <>
      {/* Fixed outer shell — full-width */}
      <div
        id="navbar-shell"
        className="fixed top-0 left-0 right-0 z-50"
      >
        {/* Full-width navbar — fixed height */}
        <nav
          style={{
            background: "#0a0a0a",
            borderBottom: "1px solid #1c1c1c",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            gap: "2px",
            width: "100%",
            height: 52,
          }}
        >
          {/* Logo */}
          <Link
            href="/"
            className="select-none flex-shrink-0 mr-3 flex items-center"
            style={{ padding: "4px 8px" }}
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/hoops-logo.svg"
              alt="Hoops Finance"
              style={{ height: 24 }}
            />
          </Link>

          {/* Desktop nav items */}
          <div className="hidden sm:flex items-center justify-center gap-[2px] flex-1">
            {/* Markets dropdown */}
            <div ref={marketsRef} className="relative">
              <button
                onClick={() => { setMarketsOpen((v) => !v); }}
                className={[
                  navItemCls(isMarketsActive),
                  "inline-flex items-center gap-[5px] cursor-pointer",
                ].join(" ")}
              >
                Markets
                <svg
                  width="9"
                  height="9"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  className="flex-shrink-0"
                  style={{
                    transition: "transform 200ms ease",
                    transform: marketsOpen ? "rotate(180deg)" : "rotate(0deg)",
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Rich markets dropdown */}
              <div
                className="absolute z-50"
                style={{
                  top: "calc(100% + 8px)",
                  left: 0,
                  width: "320px",
                  background: "#0d0d0d",
                  border: "1px solid #1c1c1c",
                  borderRadius: "14px",
                  boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                  transition: "opacity 200ms ease, transform 200ms ease",
                  opacity: marketsOpen ? 1 : 0,
                  transform: marketsOpen
                    ? "translateY(0)"
                    : "translateY(-6px)",
                  pointerEvents: marketsOpen ? "auto" : "none",
                }}
              >
                {/* Dropdown header */}
                <div
                  style={{
                    padding: "12px 14px 10px",
                    borderBottom: "1px solid #1c1c1c",
                  }}
                >
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#4a4a4a",
                      letterSpacing: "0.14em",
                      textTransform: "uppercase",
                    }}
                  >
                    Markets
                  </span>
                </div>

                {/* Dropdown items */}
                {MARKET_ITEMS.map((item, idx) => {
                  const isActive =
                    pathname === item.path ||
                    pathname.startsWith(item.path + "/");
                  const isHovered = marketsHover === item.path;
                  const isLast = idx === MARKET_ITEMS.length - 1;

                  return (
                    <Link
                      key={item.path}
                      href={item.path}
                      onMouseEnter={() => { setMarketsHover(item.path); }}
                      onMouseLeave={() => { setMarketsHover(null); }}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "14px",
                        padding: "13px 14px",
                        borderBottom: isLast ? "none" : "1px solid #0f0f0f",
                        background: isHovered ? "#121212" : "transparent",
                        transition: "background 150ms ease",
                        textDecoration: "none",
                        borderRadius: isLast ? "0 0 14px 14px" : undefined,
                      }}
                    >
                      {/* Icon box — icon spins 360° when dropdown opens, staggered */}
                      <div
                        style={{
                          width: 38,
                          height: 38,
                          borderRadius: 10,
                          background: "#111",
                          border: isHovered ? `1px solid ${item.color}33` : "1px solid #1c1c1c",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          color: item.color,
                          transition: "border-color 200ms ease, box-shadow 200ms ease",
                          boxShadow: isHovered ? `0 0 12px ${item.color}18` : "none",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "transform 500ms cubic-bezier(0.34,1.56,0.64,1)",
                            transform: marketsOpen ? "rotate(360deg)" : "rotate(0deg)",
                            transitionDelay: marketsOpen ? `${String(idx * 80)}ms` : "0ms",
                          }}
                        >
                          {item.icon}
                        </div>
                      </div>

                      {/* Text */}
                      <div className="flex-1 min-w-0">
                        <div
                          style={{
                            fontSize: "12px",
                            fontWeight: 500,
                            color: isActive ? "#d4cfc6" : "#c8c3ba",
                            lineHeight: 1.2,
                          }}
                        >
                          {item.name}
                        </div>
                        <div
                          style={{
                            fontSize: "10px",
                            color: "#4a4a4a",
                            marginTop: "2px",
                            lineHeight: 1.3,
                          }}
                        >
                          {item.description}
                        </div>
                      </div>

                      {/* Hover arrow */}
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        style={{
                          flexShrink: 0,
                          color: "#333",
                          opacity: isHovered ? 1 : 0,
                          transition: "opacity 150ms ease",
                        }}
                      >
                        <path
                          d="M3 7h8M8 4l3 3-3 3"
                          stroke="currentColor"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* Explorer (public — address-based position viewer) */}
            <Link href="/explorer/wallets" className={navItemCls(isExplorerActive)}>
              Explorer
            </Link>

            {/* Swap */}
            <Link href="/swap" className={navItemCls(isSwapActive)}>
              Swap
            </Link>

            {/* Strategies */}
            <Link href="/strategies" className={navItemCls(isStrategiesActive)}>
              Strategies
            </Link>

            {/* Assistant + Chats dropdown */}
            <div
              ref={assistantRef}
              className="relative"
              style={{ position: "relative" }}
              onMouseEnter={() => { if (assistantCloseTimer.current) clearTimeout(assistantCloseTimer.current); if (isLoggedIn) setAssistantOpen(true); }}
              onMouseLeave={() => { assistantCloseTimer.current = setTimeout(() => { setAssistantOpen(false); }, 150); }}
            >
              <Link href="/assistant" className={navItemCls(isAssistantActive)}>
                Assistant
              </Link>

              {/* Invisible hover bridge between trigger and dropdown */}
              {assistantOpen && (
                <div style={{ position: "absolute", top: "100%", left: 0, width: "100%", height: 12 }} />
              )}

              {isLoggedIn && (
                <div
                  className="absolute z-50"
                  style={{
                    top: "100%",
                    paddingTop: 12,
                    left: 0,
                    width: 260,
                    pointerEvents: assistantOpen ? "auto" : "none",
                  }}
                  onMouseEnter={() => { if (assistantCloseTimer.current) clearTimeout(assistantCloseTimer.current); }}
                  onMouseLeave={() => { assistantCloseTimer.current = setTimeout(() => { setAssistantOpen(false); }, 150); }}
                >
                 <div
                  style={{
                    background: "#0d0d0d",
                    border: "1px solid #1c1c1c",
                    borderRadius: 14,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                    transition: "opacity 200ms ease, transform 200ms ease",
                    opacity: assistantOpen ? 1 : 0,
                    transform: assistantOpen ? "translateY(0)" : "translateY(-6px)",
                    overflow: "hidden",
                  }}
                >
                  {/* New conversation */}
                  <Link
                    href="/assistant/chat"
                    className="flex items-center gap-2 px-4 py-3 text-[12px] font-medium hover:bg-[#141414] transition-colors"
                    style={{ color: "var(--accent)", borderBottom: "1px solid #1c1c1c" }}
                  >
                    <svg width="12" height="12" viewBox="0 0 16 16" fill="none"><line x1="8" y1="3" x2="8" y2="13" stroke="currentColor" strokeWidth="1.5"/><line x1="3" y1="8" x2="13" y2="8" stroke="currentColor" strokeWidth="1.5"/></svg>
                    New conversation
                  </Link>

                  {/* Recent chats */}
                  <div className="max-h-48 overflow-y-auto">
                    {assistantSessions.length === 0 && assistantLoaded && (
                      <p className="text-[11px] px-4 py-3 text-center" style={{ color: "#555" }}>No conversations yet</p>
                    )}
                    {assistantSessions.map((s) => (
                      <Link
                        key={s._id}
                        href={`/assistant/chat/${s._id}`}
                        className="flex items-center gap-2.5 px-4 py-2.5 hover:bg-[#141414] transition-colors"
                      >
                        {/* hn-comment-dots-solid */}
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#555" className="flex-shrink-0">
                          <path d="M21,2H3V3H2V4H1V16H2V17H3V18H7V19H8V20H9V21H10V22H11V19H21V18H22V17H23V4H22V3H21V2ZM21,17H9V18H8V19H7V17H3V4H21V17ZM7,9H9V11H7V9ZM11,9H13V11H11V9ZM15,9H17V11H15V9Z"/>
                        </svg>
                        <span className="text-[12px] truncate flex-1" style={{ color: "#aaa" }}>{s.title}</span>
                      </Link>
                    ))}
                  </div>

                  {/* All conversations */}
                  <Link
                    href="/assistant"
                    className="block px-4 py-2.5 text-[11px] text-center hover:bg-[#141414] transition-colors"
                    style={{ color: "#666", borderTop: "1px solid #1c1c1c" }}
                  >
                    All conversations
                  </Link>
                </div>
                </div>
              )}
            </div>

            {/* Portfolio */}
            <Link href="/portfolio" className={navItemCls(pathname.startsWith("/portfolio"))}>
              Portfolio
            </Link>
          </div>


          {/* Right: Desktop user card / CTA */}
          <div className="hidden sm:flex items-center flex-shrink-0 gap-2" style={{ minWidth: 260 }}>
            {isLoggedIn && <NavCreditsWidget />}
            {isLoggedIn ? (
              <div ref={userMenuRef} className="relative w-full">
                {/* Wallet card trigger */}
                <button
                  onClick={() => { setUserMenuOpen((v) => !v); }}
                  className="w-full flex items-center gap-3 px-3 py-1.5 rounded-xl transition-colors duration-200 cursor-pointer select-none"
                  style={{ background: "#0d0d0d" }}
                  aria-label="Open user menu"
                  aria-expanded={userMenuOpen}
                  aria-haspopup="true"
                >
                  {/* Avatar */}
                  <Image
                    src={avatarUrl}
                    alt={displayName}
                    width={30}
                    height={30}
                    className="rounded-full object-cover flex-shrink-0"
                    style={{ width: 30, height: 30 }}
                    unoptimized
                  />

                  {/* Info block */}
                  <div className="flex flex-col items-start min-w-0">
                    <span
                      className="text-[15px] leading-tight italic truncate max-w-[120px]"
                      style={{
                        fontFamily:
                          "var(--font-instrument-serif), Georgia, serif",
                        color: "#f0ece4",
                      }}
                    >
                      {displayName.split("@")[0] || "Welcome"}
                    </span>
                    <span className="flex items-center gap-[6px] mt-0.5">
                      <span
                        className="w-[7px] h-[7px] rounded-full flex-shrink-0"
                        style={{
                          background: hasWallet ? "#3ecf7e" : "#e05252",
                          boxShadow: hasWallet
                            ? "0 0 6px rgba(62,207,126,0.53)"
                            : "0 0 6px rgba(224,82,82,0.53)",
                        }}
                      />
                      <span
                        className="text-[11px] tracking-[0.02em]"
                        style={{ color: "#8a8a8a" }}
                      >
                        {hasWallet && smartAccountAddress
                          ? `${smartAccountAddress.slice(0, 4)}….${smartAccountAddress.slice(-4)}`
                          : "Inactive wallet"}
                      </span>
                    </span>
                  </div>

                  {/* Chevron */}
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    className="flex-shrink-0"
                    style={{
                      color: "#555",
                      transition: "transform 200ms ease",
                      transform: userMenuOpen
                        ? "rotate(180deg)"
                        : "rotate(0deg)",
                    }}
                  >
                    <path
                      d="M4 6l4 4 4-4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>

                {/* User dropdown — same width as card */}
                <div
                  className="absolute right-0 overflow-hidden z-50 w-full"
                  style={{
                    top: "calc(100% + 8px)",
                    borderRadius: "14px",
                    background: "#0d0d0d",
                    border: "1px solid #1e1e1e",
                    transition: "opacity 200ms ease, transform 200ms ease",
                    opacity: userMenuOpen ? 1 : 0,
                    transform: userMenuOpen ? "translateY(0)" : "translateY(-6px)",
                    pointerEvents: userMenuOpen ? "auto" : "none",
                    boxShadow: "0 16px 40px rgba(0,0,0,0.4)",
                  }}
                >
                  {[
                    {
                      href: "/portfolio",
                      label: "Portfolio",
                      desc: "Portfolio overview and balances",
                      icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><rect x="2" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="2" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="2" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/><rect x="9" y="9" width="5" height="5" rx="1" stroke="currentColor" strokeWidth="1.3"/></svg>,
                    },
                    {
                      href: "/profile",
                      label: "Profile",
                      desc: "Account settings and preferences",
                      icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="8" cy="6" r="3" stroke="currentColor" strokeWidth="1.3"/><path d="M2 14c0-3.314 2.686-5 6-5s6 1.686 6 5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
                    },
                    {
                      href: "/developer",
                      label: "Developer",
                      desc: "API keys and integrations",
                      icon: <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><polyline points="5,4 2,8 5,12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><polyline points="11,4 14,8 11,12" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><line x1="9" y1="3" x2="7" y2="13" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>,
                    },
                  ].map((item, i, arr) => {
                    const isActive = pathname === item.href;
                    const isLast = i === arr.length - 1;
                    return (
                      <Link
                        key={item.href}
                        href={item.href}
                        className="flex items-center gap-3 transition-colors duration-150"
                        style={{
                          padding: "12px 16px",
                          borderBottom: isLast ? "none" : "1px solid #161616",
                          color: isActive ? "var(--accent)" : "#8a8a8a",
                          textDecoration: "none",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = "#121212"; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = ""; }}
                      >
                        <div className="opacity-50 flex-shrink-0">{item.icon}</div>
                        <div className="flex-1 min-w-0">
                          <div style={{ fontSize: 12, fontWeight: 500, lineHeight: 1.2 }}>{item.label}</div>
                          <div style={{ fontSize: 10, color: "#4a4a4a", marginTop: 2 }}>{item.desc}</div>
                        </div>
                      </Link>
                    );
                  })}

                  {/* Divider */}
                  <div className="h-px my-[2px]" style={{ background: "#1a1a1a" }} />

                  {/* Logout */}
                  <form action={logoutAction}>
                    <button
                      type="submit"
                      className="w-full flex items-center gap-3 text-left transition-colors duration-150"
                      style={{ padding: "12px 16px", color: "#7a3f3f" }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = "#150d0d"; e.currentTarget.style.color = "#e05252"; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = ""; e.currentTarget.style.color = "#7a3f3f"; }}
                    >
                      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" className="opacity-50 flex-shrink-0"><path d="M6 2H3a1 1 0 00-1 1v10a1 1 0 001 1h3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/><polyline points="10,5 14,8 10,11" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/><line x1="14" y1="8" x2="6" y2="8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/></svg>
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500 }}>Logout</div>
                        <div style={{ fontSize: 10, color: "#4a2a2a", marginTop: 2 }}>End your session</div>
                      </div>
                    </button>
                  </form>
                </div>
              </div>
            ) : (
              <Link
                href="/auth"
                className="group relative inline-flex items-center justify-center gap-2 h-9 px-5 text-[11px] tracking-[0.08em] font-semibold rounded-full overflow-hidden ml-auto"
                style={{ color: "#051a0e" }}
                onMouseEnter={(e) => {
                  const el = e.currentTarget;
                  el.style.transform = "translateY(-1px)";
                  el.style.boxShadow = "0 4px 20px rgba(200,255,94,0.3), 0 0 0 1px rgba(200,255,94,0.15)";
                }}
                onMouseLeave={(e) => {
                  const el = e.currentTarget;
                  el.style.transform = "translateY(0)";
                  el.style.boxShadow = "0 2px 8px rgba(200,255,94,0.1)";
                }}
                onMouseDown={(e) => { e.currentTarget.style.transform = "translateY(0) scale(0.97)"; }}
                onMouseUp={(e) => { e.currentTarget.style.transform = "translateY(-1px)"; }}
              >
                {/* Gradient background — primary accent */}
                <span
                  className="absolute inset-0 transition-opacity duration-300"
                  style={{
                    background: "linear-gradient(135deg, #c8ff5e 0%, #d4ff80 50%, #c8ff5e 100%)",
                    backgroundSize: "200% 200%",
                  }}
                />
                {/* Shimmer sweep on hover */}
                <span
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{
                    background: "linear-gradient(105deg, transparent 30%, rgba(255,255,255,0.2) 50%, transparent 70%)",
                    backgroundSize: "200% 100%",
                    animation: "shimmer 1.5s ease-in-out infinite",
                  }}
                />
                {/* Arrow icon */}
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                  className="relative z-10 transition-transform duration-200 group-hover:translate-x-[2px]"
                >
                  <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="relative z-10">Start Earning</span>
              </Link>
            )}
          </div>

          {/* Mobile: Hamburger button */}
          <button
            onClick={() => { setMobileOpen((v) => !v); }}
            className="sm:hidden flex flex-col items-center justify-center w-8 h-8 gap-[5px] ml-auto"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
          >
            <span
              className="block w-4 h-[1.5px] transition-all duration-200"
              style={{
                background: "#666",
                transform: mobileOpen
                  ? "rotate(45deg) translateY(6.5px)"
                  : "none",
              }}
            />
            <span
              className="block w-4 h-[1.5px] transition-all duration-200"
              style={{
                background: "#666",
                opacity: mobileOpen ? 0 : 1,
              }}
            />
            <span
              className="block w-4 h-[1.5px] transition-all duration-200"
              style={{
                background: "#666",
                transform: mobileOpen
                  ? "rotate(-45deg) translateY(-6.5px)"
                  : "none",
              }}
            />
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-40 sm:hidden"
            onClick={() => { setMobileOpen(false); }}
          />
          <div
            className="fixed top-0 right-0 bottom-0 w-[280px] z-50 sm:hidden overflow-y-auto"
            style={{
              background: "#0d0d0d",
              borderLeft: "1px solid #1c1c1c",
            }}
          >
            <div className="flex flex-col pt-16 px-6">
              {/* Markets section */}
              <span
                style={{
                  fontSize: "9px",
                  color: "#4a4a4a",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                Markets
              </span>
              {MARKET_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={mobileLinkCls(
                    pathname === item.path ||
                      pathname.startsWith(item.path + "/")
                  )}
                >
                  {item.name}
                </Link>
              ))}

              <div
                className="h-px my-3"
                style={{ background: "#1c1c1c" }}
              />

              {/* Main nav */}
              <Link
                href="/explorer/wallets"
                className={mobileLinkCls(isExplorerActive)}
              >
                Explorer
              </Link>
              <Link
                href="/swap"
                className={mobileLinkCls(isSwapActive)}
              >
                Swap
              </Link>
              <Link
                href="/strategies"
                className={mobileLinkCls(isStrategiesActive)}
              >
                Strategies
              </Link>
              <Link
                href="/assistant"
                className={mobileLinkCls(isAssistantActive)}
              >
                Assistant
              </Link>
              <Link
                href="/portfolio"
                className={mobileLinkCls(pathname.startsWith("/portfolio"))}
              >
                Portfolio
              </Link>

              <div
                className="h-px my-3"
                style={{ background: "#1c1c1c" }}
              />

              {/* Testnet section */}
              <span
                style={{
                  fontSize: "9px",
                  color: "#4a4a4a",
                  letterSpacing: "0.14em",
                  textTransform: "uppercase",
                  marginBottom: "4px",
                }}
              >
                Testnet
              </span>
              {TESTNET_ITEMS.map((item) => (
                <Link
                  key={item.path}
                  href={item.path}
                  className={mobileLinkCls(
                    pathname === item.path ||
                      pathname.startsWith(item.path + "/")
                  )}
                >
                  {item.name}
                </Link>
              ))}

              <div
                className="h-px my-3"
                style={{ background: "#1c1c1c" }}
              />

              {/* Auth section */}
              {isLoggedIn ? (
                <>
                  {/* User info row */}
                  <div className="flex items-center gap-2 pb-3">
                    <div className="relative flex-shrink-0">
                      <Image
                        src={avatarUrl}
                        alt={displayName}
                        width={32}
                        height={32}
                        className="rounded-full object-cover"
                        style={{ width: 32, height: 32 }}
                        unoptimized
                      />
                      <span
                        className="absolute -bottom-[2px] -right-[2px] w-[9px] h-[9px] rounded-full"
                        style={{
                          background: hasWallet ? "#3ecf7e" : "#e05252",
                          boxShadow: `0 0 0 1.5px #0d0d0d`,
                        }}
                        aria-label={hasWallet ? "Wallet connected" : "No wallet"}
                      />
                    </div>
                    <div className="min-w-0">
                      <p
                        className="text-[11px] font-medium truncate"
                        style={{ color: "#d4cfc6" }}
                      >
                        {displayName.split("@")[0] || "account"}
                      </p>
                      <p
                        className="text-[9px] tracking-[0.06em]"
                        style={{ color: "#666" }}
                      >
                        {hasWallet ? "wallet active" : "no wallet"}
                      </p>
                    </div>
                  </div>

                  <Link
                    href="/profile"
                    className={mobileLinkCls(pathname === "/profile")}
                  >
                    Profile
                  </Link>
                  <Link
                    href="/developer"
                    className={mobileLinkCls(pathname === "/developer")}
                  >
                    Developer
                  </Link>
                  <form action={logoutAction} className="mt-1">
                    <button
                      type="submit"
                      className="py-3 text-[12px] tracking-[0.08em] font-normal transition-colors"
                      style={{ color: "#7a3f3f" }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = "#e05252";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = "#7a3f3f";
                      }}
                    >
                      Log out
                    </button>
                  </form>
                </>
              ) : (
                <Link
                  href="/auth"
                  className="mt-2 relative flex items-center justify-center gap-2 h-11 text-[11px] tracking-[0.08em] font-semibold rounded-full overflow-hidden"
                  style={{ color: "#051a0e" }}
                >
                  <span
                    className="absolute inset-0"
                    style={{ background: "linear-gradient(135deg, #c8ff5e 0%, #d4ff80 50%, #c8ff5e 100%)" }}
                  />
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="relative z-10">
                    <path d="M3 7h8M8 4l3 3-3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <span className="relative z-10">Start Earning</span>
                </Link>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
